import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { message } from 'antd';
import ImageResize from 'quill-image-resize-module-react';
import { normalizeRichTextLinks } from '../utils/richText';

let imageResizeRegistered = false;
if (!imageResizeRegistered) {
  Quill.register('modules/imageResize', ImageResize);
  // 保留图片手动缩放后的宽高格式，避免重新进入编辑页时尺寸丢失。
  const Parchment = Quill.import('parchment');
  const WidthStyle = new Parchment.Attributor.Style('width', 'width');
  const HeightStyle = new Parchment.Attributor.Style('height', 'height');
  Quill.register(WidthStyle, true);
  Quill.register(HeightStyle, true);
  imageResizeRegistered = true;
}

interface RichTextEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  minHeight?: number;
  uploadImage?: (file: File) => Promise<string>;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value = '',
  onChange,
  placeholder = '请输入内容...',
  minHeight = 220,
  uploadImage,
}) => {
  const quillRef = useRef<ReactQuill | null>(null);
  const lastSyncedHtmlRef = useRef<string>('');

  const syncEditorHtml = useCallback(
    (html: string) => {
      const normalized = normalizeRichTextLinks(html || '');
      if (normalized === lastSyncedHtmlRef.current) return;
      lastSyncedHtmlRef.current = normalized;
      onChange?.(normalized);
    },
    [onChange]
  );

  useEffect(() => {
    lastSyncedHtmlRef.current = normalizeRichTextLinks(value || '');
  }, [value]);

  const handleImageUpload = async () => {
    try {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/png,image/jpeg,image/webp,image/gif');
      input.click();

      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;
        if (!uploadImage) {
          message.warning('图片上传能力未配置');
          return;
        }

        const imageUrl = await uploadImage(file);
        if (!imageUrl) {
          message.error('图片上传失败');
          return;
        }

        const editor = quillRef.current?.getEditor();
        if (!editor) return;
        const range = editor.getSelection(true);
        const index = range?.index ?? editor.getLength();
        editor.insertEmbed(index, 'image', imageUrl, 'user');
        editor.setSelection(index + 1, 0);
        syncEditorHtml(editor.root.innerHTML);
      };
    } catch (error: any) {
      message.error(error?.message || '图片上传失败');
    }
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          [{ size: ['small', false, 'large', 'huge'] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['blockquote', 'code-block'],
          ['link', 'image'],
          ['clean'],
        ],
        handlers: {
          image: handleImageUpload,
        },
      },
      imageResize: {
        // 仅启用 Resize + DisplaySize，避免 Toolbar 在部分 Quill 环境下触发 Attributor 异常。
        modules: ['Resize', 'DisplaySize'],
      },
    }),
    [uploadImage]
  );

  const formats = [
    'header',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'align',
    'list',
    'bullet',
    'blockquote',
    'code-block',
    'link',
    'image',
    'width',
    'height',
  ];

  return (
    <div className="rounded border border-gray-200 bg-white">
      <div className="rich-text-editor">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={(val) => syncEditorHtml(val)}
          onBlur={(_previousRange, _source, editor) => syncEditorHtml(editor?.getHTML?.() || '')}
          placeholder={placeholder}
          modules={modules}
          formats={formats}
          style={{ minHeight }}
        />
      </div>
      <style>{`
        .rich-text-editor .ql-editor {
          min-height: ${minHeight}px;
          font-size: 14px;
          line-height: 1.8;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;

